import { useMutation } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import { schoolRequestsApi } from '../../school-requests/api/school-requests.api';

/**
 * Marcheaza materialul video ca vazut, la atingerea pragului de 95 % din segmente (SP-03).
 *
 * 🔴 Se marcheaza la PRAG, nu la apasarea butonului. Butonul e o consecinta a pragului,
 * nu cauza lui: cine vede tot materialul si inchide fila a facut exact ce i s-a cerut, si
 * ar ramane nemarcat daca am astepta clicul.
 *
 * 🔴 Santinela e per MONTARE. Pragul se recalculeaza la fiecare render (`poateContinua`
 * e derivat), deci fara ea am trimite cate o cerere la fiecare esantion de dupa prag.
 *
 * ⚠️ Ridicarea ei la esec e DEFENSIVA si, azi, neobservabila — spus asa ca sa nu para
 * acoperita cand nu e. Cerinta („un esec sa se reincerce la urmatoarea vizita") e
 * indeplinita de montarea noua, care aduce un `useRef` nou oricum, indiferent ce face
 * `onError`. Masurat: cu resetarea SCOASA, sonda trece neschimbata — deci nicio poarta
 * de-a mea nu acopera linia asta. Ramane pentru ziua in care ceva re-declanseaza
 * `marcheaza()` in aceeasi montare (o reincercare, un al doilea prag); fara acel
 * declansator, nu are cum sa se vada.
 *
 * Ruta e idempotenta si pastreaza primul moment, deci un al doilea apel nu strica nimic;
 * santinela exista ca sa nu batem in server degeaba, nu ca sa-l protejam.
 *
 * ⚠️ Esecul NU se arata omului, si nici succesul. Directia S1, pe scurt: consemnarea e de
 * FUNDAL, nu un pas prin care trece omul — deci zero stare de asteptare pe «Continuă»,
 * zero spinner, zero `aria-live`, zero buton de „reincearca", zero text la timpul trecut
 * despre baza de date. Orice element legat de raspunsul serverului ar reintroduce pe
 * furis exact poarta pe care am convenit sa n-o punem. Confirmarea vizibila exista deja
 * si e a OMULUI: bara de segmente plina, «Ai văzut tot filmulețul», butonul activ.
 *
 * 🔴 LIMITA TACERII (S1). Tacerea e corecta azi doar pentru ca esecul e o inconsecventa
 * de raportare, nu o consecinta pentru om: consemnarea nu e poarta nici in backend (ADMIN
 * poate aproba o cerere cu `video_vazut_dt` gol), iar remontarea retrimite cererea
 * (masurat: 1 → 2). **In ziua in care consemnarea devine poarta tare — in backend, sau
 * prin ADMIN care nu mai poate aproba fara ea — tacerea trebuie sa se termine in aceeasi
 * zi.** Atunci esecul chiar schimba ce i se intampla omului, si ascunderea lui devine
 * defectul pe care il vanam de obicei.
 */
export function useMarcheazaVideoVazut() {
    const trimis = useRef(false);
    // p8: butonul «Continua» trebuie sa astepte PATCH-ul inainte de refresh de token (altfel
    // un refresh mai rapid decat raspunsul serverului ar citi tot claim-ul vechi si l-ar
    // trimite pe profesor inapoi pe video, care remonteaza cu `deschis` fals -- adica exact
    // o noua vizionare ceruta degeaba). Promisiunea se rezolva mereu (succes SAU esec): un
    // esec nu se ascunde aici, doar nu se arata omului -- resolverul, cu tokenul proaspat,
    // il trimite oricum inapoi pe video daca marcajul n-a prins.
    const promisiune = useRef<Promise<void> | null>(null);

    const mutation = useMutation({
        mutationFn: () => schoolRequestsApi.marcheazaVideoVazut(),
        onSuccess: () => {
            trimis.current = true;
        },
        onError: (error) => {
            // Ce mai ajunge aici, dupa #97: 404 (profesorul n-are cerere in editia curenta)
            // si esecurile de retea sau de server. Cererea DEJA DECISA nu mai ajunge —
            // raspunde 200 no-op si trece prin `onSuccess`. Niciuna nu e o problema a omului
            // de pe ecran, si niciuna nu redirecteaza de aici: tinta o decide resolverul
            // post-login, la urmatoarea navigare. Un al doilea decident aici ar fi exact
            // greseala din 2026-08.
            console.error('Nu am putut marca materialul video ca vazut:', error);
            trimis.current = false;
        },
    });

    // 🔴 `useCallback`: pagina o cheama dintr-un efect care are functia in dependinte.
    //    Recreata la fiecare render, efectul ar rula la fiecare render — si santinela ar
    //    fi singurul lucru care ne-ar mai salva de un potop de cereri. O plasa nu e un
    //    proiect: se leaga stabil, apoi se pune si santinela.
    const marcheaza = useCallback(() => {
        if (trimis.current) return;
        trimis.current = true;
        promisiune.current = mutation.mutateAsync().then(
            () => undefined,
            () => undefined
        );
    }, [mutation.mutateAsync]);

    /** Rezolva dupa ce PATCH-ul s-a stabilit (succes sau esec), sau imediat daca n-a plecat inca. */
    const asteaptaMarcaj = useCallback(async () => {
        if (promisiune.current) await promisiune.current;
    }, []);

    return { marcheaza, asteaptaMarcaj };
}
